import * as React from "react";

interface ZoomableImageProps {
    src: string;
    maxZoomLevel?: number;
    zoomStep?: number;
    imageStyles?: React.CSSProperties;
    alt?: string;
}

const imageContainerStyles: React.CSSProperties = {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden"
};

export const ZoomableImage: React.FC<ZoomableImageProps> = ({
    src,
    maxZoomLevel = 5,
    zoomStep = 0.002,
    imageStyles,
    alt = ""
}) => {
    const [zoomLevel, setZoomLevel] = React.useState(1);
    const [transformOrigin, setTransformOrigin] = React.useState<string | undefined>();
    const [isHovering, setIsHovering] = React.useState(false);

    const handleMouseEnter = React.useCallback(() => {
        setIsHovering(true);
    }, []);

    const handleMouseLeave = React.useCallback(() => {
        setIsHovering(false);
    }, []);

    const handleWheel = React.useCallback((e: React.WheelEvent<HTMLDivElement>) => {
        if (isHovering) {
            e.preventDefault();

            const scrollDelta = e.deltaY;

            const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - left) / width) * 100;
            const y = ((e.clientY - top) / height) * 100;

            setZoomLevel(prevZoom => {
                if (prevZoom === 1) {
                    setTransformOrigin(`${x}% ${y}%`);
                }
                const newZoom = prevZoom - scrollDelta * zoomStep;
                return Math.max(1, Math.min(newZoom, maxZoomLevel));
            });
        }
    }, [isHovering, maxZoomLevel, zoomStep]);

    const imageStyle: React.CSSProperties = React.useMemo(() => ({
        ...imageStyles,
        transition: "transform 0.2s ease-out",
        transformOrigin: transformOrigin,
        transform: `scale(${zoomLevel})`,
        willChange: "transform"
    }), [imageStyles, zoomLevel, transformOrigin]);

    return (
        <div
            style={imageContainerStyles}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onWheel={handleWheel}
        >
            <img
                src={src}
                style={imageStyle}
                alt={alt}
            />
        </div>
    );
};